import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import ThemeIcon from './ThemeIcon';
import './chatbot-animations.css';
import { startFirstChat, sendChatMessage } from '../api';
import '../styles/scrollbar.css';

const ChatbotPreview = ({ theme, iconUrl, isOpen, setIsOpen, userId, chatbotId: propChatbotId }) => {
    const { config, styles, components } = theme;
    const BackgroundEffects = components?.BackgroundEffects;
    const TypingIndicator = components?.TypingIndicator;

    // Get title and subtitle from URL params if available
    const params = new URLSearchParams(window.location.search);
    const title = params.get('name') || config.name;
    const subtitle = params.get('description') || config.description;

    // Get theme-specific scrollbar class
    const getScrollbarClass = () => {
        switch(config.id) {
            case 'dark':
                return 'scrollbar-dark';
            case 'glassmorphism':
                return 'scrollbar-glass';
            case 'emoji':
                return 'scrollbar-emoji';
            case 'synthwave':
                return 'scrollbar-synthwave';
            case 'nature':
                return 'scrollbar-nature';
            case 'hologram':
                return 'scrollbar-hologram';
            case 'professional':
            default:
                return 'scrollbar-professional';
        }
    };

    // Chat state management
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [currentOptions, setCurrentOptions] = useState([]);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [isFlowEnd, setIsFlowEnd] = useState(false);
    const [showOptions, setShowOptions] = useState(true);
    const [streamingMessage, setStreamingMessage] = useState(null);
    const [lastOptionsQuestion, setLastOptionsQuestion] = useState(null);
    const messagesEndRef = useRef(null);
    const [isHovered, setIsHovered] = useState(false);
    const [conversationId, setConversationId] = useState(null);
    const [chatbotType, setChatbotType] = useState(null);
    const [chatbotId, setChatbotId] = useState(() => {
        // Priority: props > localStorage > theme config > default
        return propChatbotId || localStorage.getItem('chatbot_id') || config.chatbotId || 'default_chatbot_id';
    });

    // Scroll chat to bottom when new messages arrive
    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);


    // Create a new message object
    const createMessage = (text, isUser, options = []) => ({
        text: typeof text === 'string' ? text : (text !== undefined && text !== null ? JSON.stringify(text) : ''),
        isUser,
        options: Array.isArray(options) ? options.map(opt => typeof opt === 'string' ? opt : (opt !== undefined && opt !== null ? JSON.stringify(opt) : '')) : [],
        timestamp: new Date()
    });

    // Add a message to chat history
    const addMessage = (message) => {
        setMessages(prev => [...prev, message]);
    };

    // Get all messages for API call
    const getAllMessages = () => {
        return messages.map(msg => ({
            role: msg.isUser ? 'user' : 'assistant',
            content: msg.text
        }));
    };

    // Handle option button click
    const handleOptionClick = (option) => {
        if (isLoading) return;
        setInputText(option);
        // Don't hide options here - let the API response handle it
        handleSendMessage(option);
    };

    // Handles the first message using /first_chat
    const sendFirstMessage = async (textToSend) => {
        // console.log('Starting first chat with chatbotId:', chatbotId);
        setIsLoading(true);
        setShowOptions(false);
        const userMessage = createMessage(textToSend, true);
        addMessage(userMessage);
        setInputText('');
        setStreamingMessage(null);
        try {
            const allMessages = [...getAllMessages(), { role: 'user', content: userMessage.text }];
            // console.log('Sending to /first_chat:', { chatbot_id: chatbotId, messages: allMessages });
            const response = await startFirstChat({ 
                chatbot_id: chatbotId, 
                messages: allMessages,
                // Don't send chatbot_type in first chat
            });
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let currentStreamingMessage = null;
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value);
                const lines = chunk.split('\n');
                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.slice(6));
                            // console.log('Received SSE data:', data);
                            if (data.type === 'start') {
                                setConversationId(data.conversation_id);
                                // console.log('Set conversation ID:', data.conversation_id);
                            } else if (data.type === 'content') {
                                setIsLoading(false);  // Set loading to false as soon as we get content
                                
                                if (data.content.startsWith('~!')) {
                                    if (currentStreamingMessage) {
                                        addMessage(currentStreamingMessage);
                                        currentStreamingMessage = null;
                                        setStreamingMessage(null);
                                    }
                                    
                                    const structuredData = JSON.parse(data.content.slice(2));
                                    const { question, options, is_flow_end, question_number, chatbot_type } = structuredData;
                                    
                                    // Set chatbot type from backend response
                                    if (chatbot_type) {
                                        setChatbotType(chatbot_type);
                                    }
                                    
                                    currentStreamingMessage = createMessage(question, false);
                                    setCurrentOptions(options);
                                    setShowOptions(true);
                                    setIsFlowEnd(is_flow_end);
                                    setQuestionIndex(question_number);
                                    setLastOptionsQuestion({ question, options });
                                } else {
                                    if (!currentStreamingMessage) {
                                        currentStreamingMessage = createMessage(data.content, false);
                                    } else {
                                        currentStreamingMessage = {
                                            ...currentStreamingMessage,
                                            text: currentStreamingMessage.text + data.content
                                        };
                                    }
                                    setStreamingMessage(currentStreamingMessage);
                                }
                            } else if (data.type === 'end') {
                                if (currentStreamingMessage) {
                                    const messageKey = currentStreamingMessage.text;
                                    addMessage(currentStreamingMessage);
                                    currentStreamingMessage = null;
                                    setStreamingMessage(null);
                                }
                                // Remove logic that repeats last question here
                                // If flow ends, clear options
                                if (isFlowEnd) {
                                    setCurrentOptions([]);
                                    setShowOptions(false);
                                }
                            }
                        } catch (e) {
                            console.error('Error parsing SSE data:', e);
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error getting response:', error);
            addMessage(createMessage('Error: Could not get response.', false));
        } finally {
            setIsLoading(false);
        }
    };

    // Handles subsequent messages using /chat
    const sendChat = async (textToSend) => {
        setIsLoading(true);
        setShowOptions(false);
        const userMessage = createMessage(textToSend, true);
        addMessage(userMessage);
        setInputText('');
        setStreamingMessage(null);

        try {
            const allMessages = [...getAllMessages(), { role: 'user', content: userMessage.text }];
            const jwtToken = localStorage.getItem('jwtToken');
            const response = await sendChatMessage({
                chatbot_id: chatbotId,
                conversation_id: conversationId,
                messages: allMessages,
                jwtToken,
                is_flow_end: isFlowEnd,
                question_index: questionIndex,
                chatbot_type: chatbotType || 'rag'
            });
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let currentStreamingMessage = null;
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value);
                const lines = chunk.split('\n');
                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.slice(6));
                            if (data.type === 'content') {
                                setIsLoading(false);
                                
                                if (data.content.startsWith('~!')) {
                                    // Handle flow response
                                    if (currentStreamingMessage) {
                                        addMessage(currentStreamingMessage);
                                        currentStreamingMessage = null;
                                        setStreamingMessage(null);
                                    }
                                    
                                    const structuredData = JSON.parse(data.content.slice(2));
                                    const { question, options, is_flow_end, question_number, chatbot_type } = structuredData;
                                    
                                    // Only update lastOptionsQuestion if we're in a flow-based chat
                                    if (chatbot_type === 'solar' || chatbot_type === 'real-estate') {
                                        setLastOptionsQuestion({ question, options });
                                    } else {
                                        setLastOptionsQuestion(null);
                                    }

                                    currentStreamingMessage = createMessage(question, false);
                                    setCurrentOptions(options);
                                    setShowOptions(options && options.length > 0 && !is_flow_end);
                                    setIsFlowEnd(is_flow_end);
                                    setQuestionIndex(question_number);
                                    setChatbotType(chatbot_type);

                                    // If flow ends, clear options
                                    if (is_flow_end) {
                                        setCurrentOptions([]);
                                        setShowOptions(false);
                                    }
                                } else {
                                    // Handle RAG or ad-hoc response
                                    if (!currentStreamingMessage) {
                                        currentStreamingMessage = createMessage(data.content, false);
                                    } else {
                                        currentStreamingMessage = {
                                            ...currentStreamingMessage,
                                            text: currentStreamingMessage.text + data.content
                                        };
                                    }
                                    setStreamingMessage(currentStreamingMessage);
                                }
                            } else if (data.type === 'end') {
                                if (currentStreamingMessage) {
                                    addMessage(currentStreamingMessage);
                                    currentStreamingMessage = null;
                                    setStreamingMessage(null);
                                }
                                // If flow ends, clear options
                                if (isFlowEnd) {
                                    setCurrentOptions([]);
                                    setShowOptions(false);
                                }
                            }
                        } catch (e) {
                            console.error('Error parsing SSE data:', e);
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error getting response:', error);
            addMessage(createMessage('Error: Could not get response.', false));
        } finally {
            setIsLoading(false);
        }
    };

    // Main handler for sending messages
    const handleSendMessage = async (forcedInput) => {
        let textToSend = forcedInput || inputText;
        textToSend = String(textToSend); // Ensure it's always a string
        if ((!textToSend.trim() && !forcedInput) || isLoading) return;
        // console.log('handleSendMessage called with:', { textToSend, conversationId, isFlowEnd, questionIndex });
        if (!conversationId) {
            await sendFirstMessage(textToSend);
        } else {
            await sendChat(textToSend);
        }
    };

    // Handle enter key press
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    // Check if we're in button-only mode (small iframe)
    const isButtonOnly = window.location.pathname === '/chatbot-button';
    
    // Handle hover for button-only mode - send message to parent
    const handleMouseEnter = () => {
        setIsHovered(true);
        if (isButtonOnly && window.parent !== window) {
            window.parent.postMessage({ type: 'showTooltip' }, '*');
        }
    };
    
    const handleMouseLeave = () => {
        setIsHovered(false);
        if (isButtonOnly && window.parent !== window) {
            window.parent.postMessage({ type: 'hideTooltip' }, '*');
        }
    };

    return (
        <div className={isButtonOnly ? "w-full h-full flex items-end justify-end" : "fixed bottom-5 right-5 z-50 max-w-[calc(100vw-2.5rem)] max-h-[calc(100vh-2.5rem)]"} style={isButtonOnly ? {paddingBottom: '2px', paddingRight: '8px'} : {}}>
            {!isOpen ? (
                <div
                    className="relative flex items-center"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    {/* Professional Tooltip - only show in full mode, not button-only */}
                    {!isButtonOnly && (
                        <div className={`absolute right-full mr-4 w-max transition-all duration-300 ease-in-out ${isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2 pointer-events-none'}`}>
                            <div className="relative py-3 px-4 bg-white rounded-lg shadow-2xl border border-gray-100">
                                <div className="text-sm text-left">
                                    <p className="font-bold text-gray-800">Need assistance?</p>
                                    <p className="text-gray-600">Our AI is ready to help you.</p>
                                    <p className="font-semibold text-indigo-600">Click to start a chat!</p>
                                </div>
                                <div className="absolute top-1/2 -right-1 w-2.5 h-2.5 bg-white transform rotate-45 -translate-y-1/2 border-r border-t border-gray-100"></div>
                            </div>
                        </div>
                    )}

                    {/* Widget Button */}
                    <button
                        onClick={() => setIsOpen(true)}
                        className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 ease-in-out ${styles.avatar?.background || ''} ${styles.avatar?.border || ''} ${styles.avatar?.boxShadow || 'shadow-lg'} transform hover:scale-110`}
                    >
                        <div className="relative w-full h-full flex items-center justify-center">
                            {/* Typing indicator on hover */}
                            <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
                                <div className="flex items-center justify-center space-x-1">
                                    <span className={`h-2 w-2 rounded-full ${styles.avatar?.text?.replace('text-', 'bg-') || 'bg-white'} animate-bounce [animation-delay:-0.3s]`}></span>
                                    <span className={`h-2 w-2 rounded-full ${styles.avatar?.text?.replace('text-', 'bg-') || 'bg-white'} animate-bounce [animation-delay:-0.15s]`}></span>
                                    <span className={`h-2 w-2 rounded-full ${styles.avatar?.text?.replace('text-', 'bg-') || 'bg-white'} animate-bounce`}></span>
                                </div>
                            </div>

                            {/* Default icon */}
                            <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${isHovered ? 'opacity-0' : 'opacity-100'}`}>
                                {iconUrl ? (
                                    <div className="w-12 h-12 rounded-full overflow-hidden">
                                        <img src={iconUrl} alt="Company Icon" className="w-full h-full object-cover" />
                                    </div>
                                ) : (
                                    <i className={`fas fa-${config.icon} text-3xl ${styles.avatar?.text}`}></i>
                                )}
                            </div>
                        </div>
                    </button>
                </div>
            ) : (
                <div className={`relative w-96 max-w-[calc(100vw-2.5rem)] h-[580px] max-h-[calc(100vh-2.5rem)] rounded-[20px] shadow-xl overflow-hidden ${styles.container.backgroundColor} flex flex-col min-h-0 max-md:w-[calc(100vw-2rem)] max-md:h-[480px] max-sm:w-[calc(100vw-10px)] max-sm:h-[calc(100vh-160px)]`}>
                    {/* Background Effects Layer */}
                    <div className="absolute inset-0 overflow-hidden z-0">
                    {BackgroundEffects && <BackgroundEffects />}
                    </div>

                    {/* Content Layer */}
                    <div className="relative z-10 flex flex-col h-full">
                    {/* Theme Header */}
                        <div className={`p-4 rounded-t-xl ${styles.header.background}`}>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                    <ThemeIcon iconUrl={iconUrl} config={config} styles={styles} />
                                    <div>
                                        <h3 className={`text-lg font-semibold ${styles.header.textColor}`}>{title}</h3>
                                        <p className={`${styles.header.textColor} opacity-75 text-sm`}>{subtitle}</p>
                                    </div>
                                </div>
                                <button onClick={() => {
                                    setIsOpen(false);
                                    // If we're in an iframe, notify parent to close
                                    if (window.parent !== window) {
                                        window.parent.postMessage({ type: 'closeChat' }, '*');
                                    }
                                }} className={`${styles.header.closeButtonColor} transition-colors`}>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                            </div>
                        </div>

                        {/* Chat Content with proper scrolling */}
                        <div className="flex flex-col h-full min-h-0 relative">
                            {/* Chat Messages Container */}
                            <div 
                                className={`p-6 scrollbar-hologram overflow-y-auto flex-1 min-h-0`} 
                                style={{ 
                                    fontFamily: config.typography,
                                    overflowX: (config.id === 'synthwave' || config.id === 'hologram') ? 'hidden' : undefined
                                }}
                            >
                        {/* Welcome Message */}
                        <div className="flex items-end justify-start gap-1.5 mb-4">
                            <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center ${styles.avatar.background}`}>
                                <i className={`fas fa-${config.icon} text-[10px] ${styles.avatar.text}`}></i>
                            </div>
                            <div className={`${styles.messages.bot.background} rounded-lg p-3 shadow-sm max-w-[85%] ${styles.messages.bot.text}`}>
                                <p className="text-sm" style={{ wordBreak: 'break-word' }}>{config.welcomeMessage}</p>
                            </div>
                        </div>

                        {/* Chat Messages */}
                        {messages.map((message, index) => (
                            <div key={index} className={`flex ${message.isUser ? 'justify-end' : 'items-end justify-start gap-1.5'} mb-4`}>
                                {!message.isUser && (
                                    <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center ${styles.avatar.background}`}>
                                        <i className={`fas fa-${config.icon} text-[10px] ${styles.avatar.text}`}></i>
                                    </div>
                                )}
                                <div className={`${message.isUser ? styles.messages.user.background : styles.messages.bot.background} rounded-lg p-3 shadow-sm ${message.isUser ? 'max-w-[70%]' : 'max-w-[85%]'} ${message.isUser ? styles.messages.user.text : styles.messages.bot.text}`}>
                                    <p className="text-sm" style={{ wordBreak: 'break-word' }}>{message.text}</p>
                                </div>
                            </div>
                        ))}

                        {/* Streaming Message */}
                        {streamingMessage && (
                            <div className="flex items-end justify-start gap-1.5 mb-4">
                                <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center ${styles.avatar.background}`}>
                                    <i className={`fas fa-${config.icon} text-[10px] ${styles.avatar.text}`}></i>
                                </div>
                                <div className={`${styles.messages.bot.background} rounded-lg p-3 shadow-sm max-w-[85%] ${styles.messages.bot.text}`}>
                                    <p className="text-sm" style={{ wordBreak: 'break-word' }}>{streamingMessage.text}</p>
                                </div>
                            </div>
                        )}

                        {/* Current Options */}
                        {showOptions && currentOptions && currentOptions.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2 mb-4 pl-12 pr-4">
                                {currentOptions.map((option, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleOptionClick(option)}
                                        disabled={isLoading}
                                        className={`bg-white hover:bg-gray-100 text-gray-700 text-sm py-1.5 px-3 rounded-full border border-gray-300 cursor-pointer transition-colors shadow-sm hover:shadow-md ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        {option}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Loading Indicator */}
                        {isLoading && !streamingMessage && (
                        <div className="flex items-end justify-start gap-2">
                            <div className={`${styles.typingIndicator.container} rounded-[16px] p-4 shadow-sm inline-flex items-center`}>
                                {TypingIndicator ? (
                                    <TypingIndicator />
                                ) : (
                                    <div className="flex space-x-2">
                                        <div className={`w-3 h-3 rounded-full animate-bounce ${styles.typingIndicator.dots}`}></div>
                                        <div className={`w-3 h-3 rounded-full animate-bounce ${styles.typingIndicator.dots}`} style={{ animationDelay: '0.2s' }}></div>
                                        <div className={`w-3 h-3 rounded-full animate-bounce ${styles.typingIndicator.dots}`} style={{ animationDelay: '0.4s' }}></div>
                                    </div>
                                )}
                            </div>
                            </div>
                        )}
                        
                        <div ref={messagesEndRef} />
                            </div>
                    </div>

                        {/* Input Box and Send Button - Fixed at bottom */}
                    <div className={`relative z-10 p-6 pb-7 max-sm:p-3 max-sm:pb-5 border-t ${styles.container.backgroundColor}`}>
                        <div className="flex items-center gap-3">
                            <input
                                type="text"
                                value={inputText}
                                onChange={(e) => setInputText(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder={config.id === 'emoji' ? "Type your message... 😊" : "Type your message..."}
                                className={`flex-1 px-5 py-3 max-sm:px-3 max-sm:py-2 rounded-[14px] transition-all text-base ${styles.input?.field?.background || ''} ${styles.input?.field?.text || ''} ${styles.input?.field?.border || ''} ${styles.input?.field?.focus || ''} ${styles.input?.field?.animation || ''}`}
                            />
                            <button
                                onClick={() => handleSendMessage()}
                                disabled={isLoading}
                                className={`p-3 max-sm:p-2 rounded-[14px] transition-all flex items-center justify-center min-w-[48px] max-sm:min-w-[40px] ${styles.input?.button?.background || ''} ${styles.input?.button?.text || ''} ${styles.input?.button?.animation || ''} ${styles.input?.button?.hover || ''} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                {config.id === 'emoji' ? (
                                    <span className="text-xl">✨</span>
                                ) : config.id === 'hologram' ? (
                                    <i className="fas fa-paper-plane text-cyan-100 text-lg"></i>
                                ) : config.id === 'synthwave' ? (
                                    <i className="fas fa-bolt text-fuchsia-100 text-lg"></i>
                                ) : config.id === 'nature' ? (
                                    <i className="fas fa-leaf text-emerald-100 text-lg"></i>
                                ) : (
                                    <i className="fas fa-paper-plane text-lg"></i>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className={`px-4 py-2.5 max-sm:px-2 max-sm:py-1.5 text-center text-xs ${styles.footer?.text || 'text-gray-400'}`}>
                        Built with <span className="text-red-500">❤</span> by{' '}
                        <a
                            href="https://curosai.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.footer?.link || 'text-indigo-500 hover:underline'}
                        >
                            CurosAI
                        </a>
                    </div>
                    </div>
                </div>
            )}
        </div>
    );
};

ChatbotPreview.propTypes = {
    theme: PropTypes.shape({
        config: PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            icon: PropTypes.string.isRequired,
            color: PropTypes.string.isRequired,
            welcomeMessage: PropTypes.string.isRequired,
            typography: PropTypes.string.isRequired,
            description: PropTypes.string,
            preview: PropTypes.string,
            suggestedPrompts: PropTypes.arrayOf(PropTypes.shape({ text: PropTypes.string })),
            chatbotId: PropTypes.string
        }).isRequired,
        styles: PropTypes.shape({
            container: PropTypes.object.isRequired,
            header: PropTypes.object.isRequired,
            avatar: PropTypes.object.isRequired,
            messages: PropTypes.shape({
                bot: PropTypes.object.isRequired,
                user: PropTypes.object.isRequired
            }).isRequired,
            typingIndicator: PropTypes.object.isRequired,
            footer: PropTypes.object
        }).isRequired,
        components: PropTypes.shape({
            TypingIndicator: PropTypes.elementType,
            BackgroundEffects: PropTypes.elementType
        })
    }).isRequired,
    iconUrl: PropTypes.string,
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    userId: PropTypes.string,
    chatbotId: PropTypes.string
};

export default ChatbotPreview; 